import React, { useMemo } from 'react';
import { cn } from '@h2/ui';
import Icon from '../ui/icon';
import { useSidebarStore } from '../../stores/useSidebarStore';
import { useChatStore } from '../../stores/useChatStore';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useMousePosition } from '../../hooks/useMousePosition';
import easterEggLeft from '../../assets/easter-egg-left.png';
import easterEggCenter from '../../assets/easter-egg-center.png';
import easterEggRight from '../../assets/easter-egg-right.png';

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className }) => {
  const { activeMenu, menuItems } = useSidebarStore();
  const { theme, toggleTheme } = useDarkMode();
  const { toggleChat, chatRooms } = useChatStore();
  const { toggleNotification, notifications } = useNotificationStore();
  const { screenRegion } = useMousePosition();

  // Find current menu item for breadcrumb
  const currentMenuItem = menuItems.find(item => 
    item.id === activeMenu || 
    item.children?.some(child => child.id === activeMenu)
  );

  const currentSubMenuItem = currentMenuItem?.children?.find(child => child.id === activeMenu);

  // 마우스 위치에 따라 이스터에그 이미지 선택
  const easterEggImage = useMemo(() => {
    switch (screenRegion) {
      case 'left':
        return easterEggLeft;
      case 'center':
        return easterEggCenter;
      case 'right':
        return easterEggRight;
      default:
        return easterEggCenter;
    }
  }, [screenRegion]);

  return (
    <header className={cn('bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4', className)}>
      <div className="flex items-center justify-between">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <span>Home</span>
            <Icon name="ChevronRight" size={16} className="text-gray-400 dark:text-gray-500" />
            {currentMenuItem && (
              <>
                <span className="font-medium text-gray-900 dark:text-gray-100">{currentMenuItem.label}</span>
                {currentSubMenuItem && (
                  <>
                    <Icon name="ChevronRight" size={16} className="text-gray-400 dark:text-gray-500" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">{currentSubMenuItem.label}</span>
                  </>
                )}
              </>
            )}
          </nav>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={`Current theme: ${theme}`}
          >
            <Icon name={theme === 'dark' ? 'Moon' : 'Sun'} size={20} className="text-gray-600 dark:text-gray-300" />
          </button>

          {/* Notifications */}
          <button 
            onClick={toggleNotification}
            className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="알림"
          >
            <Icon name="Bell" size={20} className="text-gray-600 dark:text-gray-300" />
            {notifications.some(n => !n.isRead) && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>

          {/* Chat */}
          <button 
            onClick={toggleChat}
            className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" 
            title="채팅"
          >
            <Icon name="MessageCircle" size={20} className="text-gray-600 dark:text-gray-300" />
            {chatRooms.some(room => room.unreadCount > 0) && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>
            )}
          </button>

          {/* Profile */}
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              <img 
                src={easterEggImage} 
                alt="User Avatar" 
                className="w-full h-full object-cover transition-all duration-300 ease-in-out"
                title={`마우스 위치: ${screenRegion}`}
              />
            </div>
            <div className="text-sm">
              <div className="font-medium text-gray-900 dark:text-gray-100">Admin User</div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
